import { useMemo } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const circle = (cx, cy, rx, ry, n = 10) =>
  Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    return [cx + rx * Math.cos(a), cy + ry * Math.sin(a)];
  });

export default function Constellation() {
  const letters = useMemo(() => {
    const N = {
      pts: [
        [0, 220],
        [0, 20],
        [100, 220],
        [100, 20],
      ],
      lines: [
        [0, 1],
        [1, 2],
        [2, 3],
      ],
    };
    const o1 = circle(200, 120, 55, 95);
    const O1 = { pts: o1, lines: o1.map((_, i) => [i, (i + 1) % 10]) };
    const o2 = circle(350, 120, 55, 95);
    const O2 = { pts: o2, lines: o2.map((_, i) => [i, (i + 1) % 10]) };
    const R = {
      pts: [
        [480, 220],
        [480, 20],
        [560, 20],
        [580, 70],
        [560, 115],
        [480, 115],
        [585, 220],
      ],
      lines: [
        [0, 1],
        [1, 2],
        [2, 3],
        [3, 4],
        [4, 5],
        [5, 6],
      ],
    };
    return [N, O1, O2, R];
  }, []);

  return (
    <section
      id="constellation-section"
      data-testid="constellation-section"
      className="relative z-10 px-6 py-24 text-center"
    >
      <p className="text-sm uppercase tracking-[0.2em] font-medium text-[#FFD700] flex items-center justify-center gap-2">
        <Sparkles className="w-4 h-4" /> Look up
      </p>
      <h2
        className="mt-4 text-4xl sm:text-6xl text-white"
        style={{ fontFamily: "'Caveat', cursive" }}
        data-testid="constellation-heading"
      >
        Even the stars spell your name
      </h2>
      <svg viewBox="-20 0 680 240" className="mx-auto mt-12 w-full max-w-2xl" aria-hidden="true">
        {letters.map((L, li) => (
          <g key={li}>
            {L.lines.map(([a, b], i) => (
              <motion.line
                key={`l${i}`}
                x1={L.pts[a][0]}
                y1={L.pts[a][1]}
                x2={L.pts[b][0]}
                y2={L.pts[b][1]}
                stroke="#FFD700"
                strokeWidth="1.5"
                strokeOpacity="0.7"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: li * 0.5 + i * 0.12 }}
              />
            ))}
            {L.pts.map(([x, y], i) => (
              <motion.circle
                key={`p${i}`}
                cx={x}
                cy={y}
                r="4"
                fill="#FFF7CC"
                style={{ filter: "drop-shadow(0 0 6px #FFD700)" }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: li * 0.5 + i * 0.08, type: "spring", stiffness: 300 }}
              />
            ))}
          </g>
        ))}
      </svg>
      <p className="mt-10 text-[#D9C9E0] text-base sm:text-lg">
        N-O-O-R. Written in the sky, forever.
      </p>
    </section>
  );
}
